import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import {
  Box,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  LinearProgress
} from '@mui/material';
import AuthContext from '../../context/AuthContext';
import { useDirectUpload } from '../../hooks/useDirectUpload';
import {
  BasicReleaseInfo,
  TrackMetadata,
  TracklistOrganization,
  // RoyaltySplits, // Hidden for now, can be re-enabled later
  ReleaseDetails,
  CoverArtwork,
  FinalVerification
} from '../../components/release';

const API_URL = process.env.REACT_APP_API_URL;

const RELEASE_TYPES = [
  { value: 'single', label: 'Single', minTracks: 1, maxTracks: 1 },
  { value: 'ep', label: 'EP', minTracks: 2, maxTracks: 6 },
  { value: 'album', label: 'Album', minTracks: 7, maxTracks: 20 }
];

const UPLOAD_STEPS = [
  'Basic Release Info',
  'Tracks & Metadata',
  'Track Order',
  // 'Royalty Configuration', // Hidden for now, can be re-enabled later
  'Release Details',
  'Cover Artwork',
  'Final Verification'
];

const UploadReleasePage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Initialize direct upload hook
  const token = localStorage.getItem('token');
  const { 
    uploadFiles, 
    isUploading, 
    uploadError,
    getOverallProgress,
    resetUpload 
  } = useDirectUpload(token);

  // Form state
  const [releaseData, setReleaseData] = useState({
    title: '',
    type: 'single',
    primaryGenre: '',
    secondaryGenres: [],
    releaseYear: new Date().getFullYear(),
    releaseDate: new Date(),
    description: '',
    explicit: false,
    catalogNumber: '',
    label: '',
    marketingInfo: {
      preSaveDate: null,
      preSaveDateEnabled: false,
      preOrderDate: null,
      preOrderDateEnabled: false,
      marketingNotes: ''
    }
  });

  // Tracks state
  const [tracks, setTracks] = useState([]);
  const [trackMetadata, setTrackMetadata] = useState({});
  const [coverArt, setCoverArt] = useState(null);

  // Royalty splits state
  const [royaltySplits, setRoyaltySplits] = useState({});

  // Dropzone for tracks
  const { getRootProps: getTracksRootProps, getInputProps: getTracksInputProps } = useDropzone({
    accept: {
      'audio/*': ['.mp3', '.wav', '.flac', '.aac', '.ogg']
    },
    onDrop: acceptedFiles => {
      const newTracks = acceptedFiles.map(file => ({
        file,
        title: file.name.replace(/\.[^/.]+$/, ''),
        trackNumber: tracks.length + 1
      }));
      setTracks([...tracks, ...newTracks]);
      
      // Initialize metadata for new tracks
      const newMetadata = { ...trackMetadata };
      // Initialize royalty splits for new tracks
      const newSplits = { ...royaltySplits };
      
      newTracks.forEach(track => {
        // Initialize metadata
        newMetadata[track.file.name] = {
          trackType: 'original',
          featuredArtists: [{
            name: '',
            role: 'main'
          }],
          isrc: '',
          composers: [],
          publishing: {
            isCopyrightControl: true,
            copyright: new Date().getFullYear() + ' ' + (releaseData.label || user.artistName || user.username)
          },
          primaryGenre: releaseData.primaryGenre,
          secondaryGenres: []
        };

        // Initialize royalty splits with unified structure
        const hasLabel = Boolean(releaseData.label);
        const hasComposers = false; // No composers by default
        
        newSplits[track.file.name] = {
          'label': {
            name: releaseData.label,
            type: 'Label',
            share: hasLabel ? 50 : 0
          },
          'artist-0': {
            name: '',
            type: 'Artist',
            role: 'main',
            share: hasLabel ? (hasComposers ? 15 : 50) : (hasComposers ? 30 : 100)
          }
        };
      });
      
      setTrackMetadata(newMetadata);
      setRoyaltySplits(newSplits);
    }
  });

  // Dropzone for cover art
  const { getRootProps: getCoverRootProps, getInputProps: getCoverInputProps } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    maxFiles: 1,
    onDrop: acceptedFiles => {
      if (acceptedFiles.length > 0) {
        // Validate image dimensions and size
        const file = acceptedFiles[0];
        const img = new Image();
        img.onload = () => {
          if (img.width < 3000 || img.height < 3000) {
            setError('Cover art must be at least 3000x3000 pixels');
          } else {
            setCoverArt(file);
            setError(null);
          }
        };
        img.src = URL.createObjectURL(file);
      }
    }
  });

  const handleNext = () => {
    if (validateCurrentStep()) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const validateCurrentStep = () => {
    switch (activeStep) {
      case 0: // Basic Release Info
        if (!releaseData.title || !releaseData.type || !releaseData.primaryGenre) {
          setError('Please fill in all required fields');
          return false;
        }
        break;
      case 1: // Tracks & Metadata
        const selectedType = RELEASE_TYPES.find(type => type.value === releaseData.type);
        if (tracks.length < selectedType.minTracks || tracks.length > selectedType.maxTracks) {
          setError(`A ${selectedType.label} must have between ${selectedType.minTracks} and ${selectedType.maxTracks} tracks`);
          return false;
        }
        // Validate track names and metadata for all tracks
        for (const track of tracks) {
          if (!track.title || track.title.trim() === '') {
            setError('Please provide a name for all tracks');
            return false;
          }
          const meta = trackMetadata[track.file.name];
          if (!meta || !meta.trackType || !meta.featuredArtists?.some(a => a.role === 'main' && a.name.trim() !== '')) {
            setError('Please complete metadata for all tracks and ensure each track has at least one main artist with a name');
            return false;
          }
        }
        break;
      case 4: // Cover Artwork (was case 5, shifted due to hidden royalty step)
        if (!coverArt) {
          setError('Please upload cover artwork');
          return false;
        }
        break;
      default:
        break;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      resetUpload();

      // Prepare files for direct upload
      const filesToUpload = [];
      
      // Add audio files
      tracks.forEach(track => {
        filesToUpload.push({
          file: track.file,
          fieldName: 'audioFiles'
        });
      });
      
      // Add cover art if provided
      if (coverArt) {
        filesToUpload.push({
          file: coverArt,
          fieldName: 'coverArt'
        });
      }
      
      // Upload files directly to Cloud Storage
      const uploadResults = await uploadFiles(filesToUpload);
      
      // Prepare release data for the direct upload endpoint
      const releaseDataForSubmission = {
        ...releaseData,
        releaseDate: releaseData.releaseDate.toISOString(),
        primaryGenre: JSON.stringify(releaseData.primaryGenre),
        secondaryGenres: JSON.stringify(releaseData.secondaryGenres),
        marketingInfo: JSON.stringify(releaseData.marketingInfo),
        audioFiles: uploadResults.audioFiles || [], // URLs from Cloud Storage
        coverArt: uploadResults.coverArt?.[0], // URL from Cloud Storage (if provided)
        tracksMetadata: JSON.stringify(
          tracks.map(track => ({
            ...trackMetadata[track.file.name],
            title: track.title,
            trackNumber: track.trackNumber
          }))
        ),
        royaltySplits: JSON.stringify(royaltySplits)
      };

      // Create release record using the direct upload endpoint
      const response = await axios.post(`${API_URL}/releases/direct`, releaseDataForSubmission, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      navigate(`/release/${response.data._id}`);
    } catch (err) {
      const errorMessage = uploadError || err.response?.data?.message || err.message || 'Failed to upload release';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <BasicReleaseInfo 
            releaseData={releaseData}
            setReleaseData={setReleaseData}
          />
        );
      case 1:
        return (
          <TrackMetadata
            tracks={tracks}
            setTracks={setTracks}
            trackMetadata={trackMetadata}
            setTrackMetadata={setTrackMetadata}
            getTracksRootProps={getTracksRootProps}
            getTracksInputProps={getTracksInputProps}
            releaseType={releaseData.type}
          />
        );
      case 2:
        return (
          <TracklistOrganization
            tracks={tracks}
            setTracks={setTracks}
          />
        );
      // case 3: Royalty Configuration - Hidden for now
      // return (
      //   <RoyaltySplits
      //     tracks={tracks}
      //     trackMetadata={trackMetadata}
      //     royaltySplits={royaltySplits}
      //     setRoyaltySplits={setRoyaltySplits}
      //   />
      // );
      case 3: // Was case 4
        return (
          <ReleaseDetails
            releaseData={releaseData}
            setReleaseData={setReleaseData}
          />
        );
      case 4: // Was case 5
        return (
          <CoverArtwork
            coverArt={coverArt}
            getCoverRootProps={getCoverRootProps}
            getCoverInputProps={getCoverInputProps}
          />
        );
      case 5: // Was case 6
        return (
          <FinalVerification
            releaseData={releaseData}
            tracks={tracks}
            trackMetadata={trackMetadata}
            royaltySplits={royaltySplits}
            coverArt={coverArt}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#e4e4e4' }}>
        Upload New Release
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Upload Progress */}
      {(isUploading || loading) && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {isUploading ? `Uploading files... ${getOverallProgress()}%` : 'Processing release...'}
          </Typography>
          <LinearProgress 
            variant={isUploading ? "determinate" : "indeterminate"} 
            value={isUploading ? getOverallProgress() : undefined}
          />
        </Box>
      )}

      <Stepper activeStep={activeStep} orientation="vertical">
        {UPLOAD_STEPS.map((label, index) => (
          <Step key={label}>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label.Mui-active': {
                  color: '#e4e4e4'
                }
              }}
            >
              {label}
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 3 }}>
                {getStepContent(index)}
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  disabled={index === 0}
                  onClick={handleBack}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={index === UPLOAD_STEPS.length - 1 ? handleSubmit : handleNext}
                  disabled={isUploading || loading}
                >
                  {isUploading ? 'Uploading Files...' : loading ? 'Processing...' : (index === UPLOAD_STEPS.length - 1 ? 'Submit' : 'Next')}
                </Button>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default UploadReleasePage;